import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Trash, Plus, ArrowsClockwise } from "@phosphor-icons/react";
import OwnerRequests from "@/components/OwnerRequests";

export default function Admin() {
  const { user } = useAuth();
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [type, setType] = useState("greenhouse");
  const [token, setToken] = useState("");
  const [country, setCountry] = useState("gb");
  const [keywords, setKeywords] = useState("");
  const [busy, setBusy] = useState(false);
  const isAgg = type === "adzuna" || type === "jooble";

  const load = () => {
    api.get("/admin/sources").then((r) => setSources(r.data.sources)).catch(() => {});
    api.get("/admin/ingestion-runs").then((r) => setRuns(r.data.runs)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const add = async () => {
    if (!isAgg && !token.trim()) return;
    const payload = isAgg
      ? { type, config: { country, keywords }, label: `${type[0].toUpperCase() + type.slice(1)} · ${keywords || country}` }
      : { type, company_token: token.trim() };
    try { await api.post("/admin/sources", payload); setToken(""); setKeywords(""); toast.success("Source added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (s) => { await api.put(`/admin/sources/${s.id}`, { enabled: !s.enabled }); load(); };
  const del = async (id) => { await api.delete(`/admin/sources/${id}`); toast("Source removed"); load(); };
  const runOne = async (s) => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/sources/${s.id}/run`); const r = data.result; toast.success(`${r.source}: +${r.new} new, ${r.deduped} deduped, ${r.expired} expired${r.errors ? ", errors" : ""}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const runAll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/ingest/run"); const a = data.run; toast.success(`Ingested ${a.new} new / ${a.updated} updated / ${a.deduped} deduped / ${a.expired} expired`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Admin" subtitle="Job sources, automated ingestion and incoming help requests. Public ATS feeds only — no scraping."
        action={<Button onClick={runAll} disabled={busy} data-testid="run-all-btn" className="bg-slate-900 hover:bg-slate-800 gap-2"><ArrowsClockwise weight="bold" /> {busy ? "Running…" : "Run now (all)"}</Button>} />

      <Tabs defaultValue="sources">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="sources" data-testid="tab-sources">Sources</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Ingestion runs</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="requests"><OwnerRequests /></TabsContent>

        <TabsContent value="sources">
          <div className="rounded-lg border border-slate-200 bg-white p-5 mb-5 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="add-source-form">
            <div className="sm:w-48">
              <Label>ATS type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5" data-testid="source-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="greenhouse">Greenhouse</SelectItem>
                  <SelectItem value="lever">Lever</SelectItem>
                  <SelectItem value="smartrecruiters">SmartRecruiters</SelectItem>
                  <SelectItem value="adzuna">Adzuna (aggregator)</SelectItem>
                  <SelectItem value="jooble">Jooble (aggregator)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAgg ? (
              <>
                {type === "adzuna" && (
                  <div className="sm:w-28">
                    <Label>Country</Label>
                    <Input className="mt-1.5" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="gb" data-testid="source-country-input" />
                  </div>
                )}
                <div className="flex-1">
                  <Label>Keywords</Label>
                  <Input className="mt-1.5" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="e.g. remote research" data-testid="source-keywords-input" />
                </div>
              </>
            ) : (
              <div className="flex-1">
                <Label>Company board token</Label>
                <Input className="mt-1.5" value={token} onChange={(e) => setToken(e.target.value)} placeholder="e.g. stripe" data-testid="source-token-input" />
              </div>
            )}
            <Button onClick={add} data-testid="add-source-btn" className="bg-slate-900 hover:bg-slate-800 gap-1.5"><Plus weight="bold" /> Add source</Button>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="sources-list">
            {sources.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 sm:gap-4 px-5 py-3.5 border-b border-slate-100 last:border-0" data-testid={`source-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{s.label}</p>
                  <p className="text-xs text-slate-500 font-mono-num">{s.type} · {s.company_token} · {s.active_count}/{s.job_count} active{s.last_run_at ? ` · last run ${s.last_run_at.slice(0, 16).replace("T", " ")}` : ""}</p>
                </div>
                <Switch checked={s.enabled} onCheckedChange={() => toggle(s)} data-testid={`toggle-source-${s.id}`} />
                <Button variant="outline" size="sm" onClick={() => runOne(s)} disabled={busy} className="border-slate-300 gap-1" data-testid={`run-source-${s.id}`}><Play /> Run</Button>
                <Button variant="ghost" size="icon" onClick={() => del(s.id)} className="text-slate-400 hover:text-red-600"><Trash /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="runs">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="runs-list">
            {runs.length === 0 && <p className="p-6 text-sm text-slate-500">No ingestion runs yet.</p>}
            {runs.map((r) => (
              <div key={r.id} className="px-5 py-4 border-b border-slate-100 last:border-0" data-testid={`run-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-800">{r.started_at?.slice(0, 16).replace("T", " ")} <span className="text-slate-400 font-normal">· {r.triggered_by}</span></p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="font-mono-num">+{r.new} new</Badge>
                    <Badge variant="secondary" className="font-mono-num">{r.deduped} deduped</Badge>
                    <Badge variant="secondary" className="font-mono-num">{r.expired} expired</Badge>
                    {r.screened_out > 0 && <Badge variant="secondary" className="font-mono-num" data-testid="run-screened-out">{r.screened_out} screened out</Badge>}
                    {r.stale > 0 && <Badge variant="secondary" className="font-mono-num" data-testid="run-stale">{r.stale} stale</Badge>}
                    {r.errors > 0 && <Badge className="bg-red-50 text-red-700 border border-red-200 font-mono-num">{r.errors} errors</Badge>}
                  </div>
                </div>
                <p className="mt-1 text-xs text-slate-500 font-mono-num">fetched {r.fetched} · updated {r.updated} · sources: {(r.sources || []).map((s) => `${s.token}(${s.new}/${s.errors ? "err" : "ok"})`).join(", ")}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
